import { useState } from 'react'
import { Button, Badge, Spinner, Text, makeStyles, tokens } from '@fluentui/react-components'
import { BuildingRegular, ArrowClockwiseRegular } from '@fluentui/react-icons'
import { aresService, AresSubject } from '../services/aresService'

const useStyles = makeStyles({
  panel: {
    marginTop: '24px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: '24px',
    boxShadow: tokens.shadow4,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: '10px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  title: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '14px 32px',
    marginTop: '16px',
  },
  label: {
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '.07em',
    color: tokens.colorNeutralForeground3,
    marginBottom: '2px',
  },
  value: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.4',
  },
  subsection: {
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '.06em',
    color: tokens.colorNeutralForeground2,
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: `1px solid ${tokens.colorNeutralBackground3}`,
  },
  timestamp: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground4,
    marginTop: '20px',
    paddingTop: '12px',
    borderTop: `1px solid ${tokens.colorNeutralBackground3}`,
  },
  errorBox: {
    marginTop: '12px',
    padding: '12px 16px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
    fontSize: '14px',
  },
  inactive: {
    color: tokens.colorPaletteRedForeground1,
  },
})

function Field({ label, value, children }: {
  label: string; value?: string | null; children?: React.ReactNode
}) {
  const s = useStyles()
  const content = children ?? value
  if (!content) return null
  return (
    <div>
      <div className={s.label}>{label}</div>
      <div className={s.value}>{content}</div>
    </div>
  )
}

function fmtDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('cs-CZ')
}

function VatBadge({ vatStatus, isVatPayer }: { vatStatus: string; isVatPayer: boolean }) {
  if (isVatPayer) return <Badge appearance="filled" color="success">Plátce DPH</Badge>
  if (vatStatus === 'NEEXISTUJICI') return <Badge appearance="filled" color="informative">Neplátce DPH</Badge>
  if (vatStatus === 'ZANIKLÝ' || vatStatus === 'ZANIKY') return <Badge appearance="filled" color="warning">Zaniklá DPH registrace</Badge>
  return <Badge appearance="filled" color="subtle">Stav DPH neznámý</Badge>
}

function SubjectBadge({ isAktivni, datumZaniku }: { isAktivni: boolean; datumZaniku: string | null }) {
  if (isAktivni) return <Badge appearance="filled" color="success">Aktivní</Badge>
  return <Badge appearance="filled" color="danger">
    {datumZaniku ? `Zaniklý ${fmtDate(datumZaniku)}` : 'Neaktivní'}
  </Badge>
}

function SubjectType({ isFyzickaOsoba }: { isFyzickaOsoba: boolean }) {
  return (
    <Badge appearance="outline" color="informative">
      {isFyzickaOsoba ? 'Fyzická osoba / OSVČ' : 'Právnická osoba'}
    </Badge>
  )
}

function AresResult({ data }: { data: AresSubject }) {
  const s = useStyles()
  const fetched = new Date(data.fetchedAt).toLocaleString('cs-CZ')

  return (
    <>
      {/* Hlavičkové odznaky */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
        <SubjectBadge isAktivni={data.isAktivni} datumZaniku={data.datumZaniku} />
        <SubjectType isFyzickaOsoba={data.isFyzickaOsoba} />
        <VatBadge vatStatus={data.vatStatus} isVatPayer={data.isVatPayer} />
      </div>

      {/* Základní data */}
      <div className={s.grid}>
        <Field label={data.isFyzickaOsoba ? 'Jméno a příjmení' : 'Obchodní název'} value={data.obchodniJmeno} />
        <Field label="IČO" value={data.ico} />
        {data.dic && <Field label="DIČ" value={data.dic} />}
        {data.pravniForma && <Field label="Právní forma (kód)" value={data.pravniForma} />}
        {data.datumVzniku && <Field label="Datum vzniku" value={fmtDate(data.datumVzniku)} />}
        {data.datumZaniku && (
          <Field label="Datum zániku">
            <span className={s.inactive}>{fmtDate(data.datumZaniku)}</span>
          </Field>
        )}
      </div>

      {/* Sídlo */}
      {data.sidlo && (
        <>
          <div className={s.subsection}>Sídlo / Místo podnikání</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: tokens.colorNeutralForeground1, marginTop: 8 }}>
            {data.sidlo}
          </div>
        </>
      )}

      {/* Časové razítko */}
      <div className={s.timestamp}>
        Data načtena: {fetched} · Zdroj: ARES (Administrativní registr ekonomických subjektů), MF ČR
      </div>
    </>
  )
}

interface Props {
  ico: string
}

export default function AresInfoPanel({ ico }: Props) {
  const s = useStyles()
  const [state, setState] = useState<
    | { status: 'idle' }
    | { status: 'loading'; step: string }
    | { status: 'ok'; data: AresSubject }
    | { status: 'error'; message: string }
  >({ status: 'idle' })

  async function load() {
    setState({ status: 'loading', step: 'Načítám z ARES…' })
    try {
      const data = await aresService.getSubject(ico)
      setState({ status: 'ok', data })
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ??
        err?.response?.data ??
        'Nepodařilo se načíst data z ARES.'
      setState({ status: 'error', message: String(msg) })
    }
  }

  return (
    <div className={s.panel}>
      <div className={s.header}>
        <Text className={s.title}>
          <BuildingRegular style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Informace o subjektu (ARES)
        </Text>
        {state.status === 'idle' ? (
          <Button appearance="primary" icon={<BuildingRegular />} onClick={load}>
            Načíst z ARES
          </Button>
        ) : state.status !== 'loading' ? (
          <Button appearance="subtle" icon={<ArrowClockwiseRegular />} onClick={load} size="small">
            Aktualizovat
          </Button>
        ) : null}
      </div>

      {state.status === 'idle' && (
        <Text style={{ color: tokens.colorNeutralForeground3, fontSize: 13, marginTop: 12, display: 'block' }}>
          Kliknutím načtete aktuální veřejné informace o subjektu z registru ARES.
        </Text>
      )}

      {state.status === 'loading' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
          <Spinner size="tiny" />
          <Text style={{ fontSize: 13, color: tokens.colorNeutralForeground3 }}>{state.step}</Text>
        </div>
      )}

      {state.status === 'error' && (
        <div className={s.errorBox}>{state.message}</div>
      )}

      {state.status === 'ok' && <AresResult data={state.data} />}
    </div>
  )
}
